import React from 'react';
import { Typography } from '@mui/material';
import type { CardReturned } from '../types';
import { currencyPrefix, shortDay } from '../logic';

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

const roas = (v: number): string => `${v.toFixed(1)}x`;

// One sentence of history for a card that re-opened after a dismissal. Examples:
//   "Back for the 2nd time. Dismissed Aug 20 by aaron: “seasonal”. 1.4x at dismissal, 1.1x now,
//    against a 1.6x break-even, about $1,240 lost in the 14 days since."
//   "Back early, 3rd time. Dismissed Aug 28, aged out with no action. Still 0.9x against a 1.6x break-even."
export function returnedSentence(r: CardReturned, store: string | null): string {
  const opener = r.trigger === 'escalation' ? `Back early, ${ordinal(r.episode)} time.` : `Back for the ${ordinal(r.episode)} time.`;
  const who = r.dismissedBy === 'watchdog' ? ', aged out with no action' : ` by ${r.dismissedBy}`;
  // The operator's reason is kept verbatim, so when it already ends a sentence the outer period is dropped.
  const dismissed = `Dismissed ${shortDay(r.dismissedAt)}${who}${r.reason ? `: \u201c${r.reason}\u201d` : ''}${/[.!?]$/.test(r.reason ?? '') ? '' : '.'}`;
  const clauses: string[] = [];
  if (r.roas30Now != null && r.breakevenRoas != null) {
    const moved = r.roas30Then != null && r.roas30Then.toFixed(1) !== r.roas30Now.toFixed(1);
    clauses.push(moved
      ? `${roas(r.roas30Then as number)} at dismissal, ${roas(r.roas30Now)} now, against a ${roas(r.breakevenRoas)} break-even`
      : `${r.roas30Then != null && r.roas30Now < r.breakevenRoas ? 'still' : 'now'} ${roas(r.roas30Now)} against a ${roas(r.breakevenRoas)} break-even`);
  }
  if (r.lossSinceUsd != null && r.lossSinceUsd > 0) {
    const window = r.dismissedDays > 30 ? 'in the last 30 days' : `in the ${r.lossWindowDays} day${r.lossWindowDays === 1 ? '' : 's'} since`;
    clauses.push(`about ${currencyPrefix(store)}${r.lossSinceUsd.toLocaleString('en-US')} lost ${window}`);
  }
  if (clauses.length === 0) return `${opener} ${dismissed}`;
  const tail = clauses.join(', ');
  return `${opener} ${dismissed} ${tail.charAt(0).toUpperCase()}${tail.slice(1)}.`;
}

export default function ReturnedLine({ returned, variant, store }: { returned: CardReturned | undefined; variant: 'row' | 'detail'; store: string | null }) {
  if (!returned) return null;
  const text = returnedSentence(returned, store);
  if (variant === 'row') {
    return (
      <Typography variant="caption" title={text} sx={{ display: 'block', mt: 0.25, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {text}
      </Typography>
    );
  }
  return <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary' }}>{text}</Typography>;
}
